import React, { useState } from 'react';

// Status types for grants and applications
export type StatusType = 
  | 'Applied' 
  | 'In Progress' 
  | 'Not Started' 
  | 'Awaiting Decision' 
  | 'Awarded' 
  | 'Awaited' 
  | 'Approved' 
  | 'Rejected'  
  | 'Draft' 
  | 'Review' 
  | 'Archived' 
  | "Saved"
  | 'Pending'
  | 'Successful'
  | 'Unsuccessful'
  | 'Awaiting Outcome';

interface StatusBadgeProps {
  status: StatusType;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'solid';
  className?: string;
}

// StatusBadge Component
export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'md',
  variant = 'default',
  className = ''
}) => {
  const getStatusStyles = () => {
    const baseStyles = {
      'Applied': { 
        bg: 'bg-[#C6FDDE]', 
        text: 'text-[#2E8B57]', 
        border: 'border-green-200', 
        solid: 'bg-green-500' 
      },
      'In Progress': { 
  bg: 'bg-orange-100', 
  text: 'text-orange-700', 
  border: 'border-orange-200', 
  solid: 'bg-orange-500' 
},
      'Not Started': { 
        bg: 'bg-[#D5DADA]', 
        text: 'text-[#777B7F]', 
        border: 'border-gray-200', 
        solid: 'bg-gray-500' 
      },
      'Awaited': { 
        bg: 'bg-[#FFEFCF]', 
        text: 'text-[#F39C12]', 
        border: 'border-yellow-200', 
        solid: 'bg-yellow-500' 
      },
      'Approved': { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-700', 
        border: 'border-emerald-200', 
        solid: 'bg-emerald-500' 
      },
      'Saved': { 
  bg: 'bg-blue-100', 
  text: 'text-blue-700', 
  border: 'border-blue-200', 
  solid: 'bg-blue-500' 
},
      'Rejected': { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        border: 'border-red-200', 
        solid: 'bg-red-500' 
      },
      'Draft': { 
        bg: 'bg-slate-100', 
        text: 'text-slate-700', 
        border: 'border-slate-200', 
        solid: 'bg-slate-500' 
      },
      'Review': { 
        bg: 'bg-orange-100', 
        text: 'text-orange-700', 
        border: 'border-orange-200', 
        solid: 'bg-orange-500' 
      },
      'Pending': { 
        bg: 'bg-purple-100', 
        text: 'text-purple-700', 
        border: 'border-purple-200', 
        solid: 'bg-purple-500' 
      },
      'Successful': { 
        bg: 'bg-green-100', 
        text: 'text-green-700', 
        border: 'border-green-200', 
        solid: 'bg-green-500' 
      },
      'Unsuccessful': { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        border: 'border-red-200', 
        solid: 'bg-red-500' 
      },
      // 'Awaiting Outcome': { 
      //   bg: 'bg-yellow-100', 
      //   text: 'text-yellow-700', 
      //   border: 'border-yellow-200', 
      //   solid: 'bg-yellow-500' 
      // },
      'Awaiting Decision': {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    border: "border-yellow-200",
    solid: "bg-yellow-500"
  },
  'Awarded': {
    bg: "bg-green-100",
    text: "text-green-700",
    border: "border-green-200",
    solid: "bg-green-600"
  },
  'Archived': {
  bg: 'bg-zinc-200',
  text: 'text-zinc-700',
  border: 'border-zinc-300',
  solid: 'bg-zinc-600'
},

'Awaiting Outcome': {
  bg: 'bg-yellow-100',
  text: 'text-yellow-700',
  border: 'border-yellow-200',
  solid: 'bg-yellow-500'
},
    };

    return baseStyles[status] || baseStyles['Not Started'];
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  const getVariantStyles = () => {
    const colors = getStatusStyles();
    
    switch (variant) {
      case 'outlined':
        return `bg-transparent ${colors.text} border-2 ${colors.border}`;
      case 'solid':
        return `${colors.solid} text-white border-transparent`;
      default:
        return `${colors.bg} ${colors.text} border ${colors.border}`;
    }
  };

  return (
    <span 
      className={`inline-flex items-center rounded-full font-medium ${getSizeStyles()} ${getVariantStyles()} transition-all duration-200 hover:scale-105 ${className}`}
    >
      {status}
    </span>
  );
};